/**
 * Éditeur de texte riche v2
 * Gère l'édition de contenu avec formatage et blocs structurés
 */
export class RichEditor {
    constructor(container) {
        this.container = container;
        // The editable area of ui:input-rich-v2. It lost its bare `content` class on 2026-03-28 and this
        // selector kept it: the editor died at init, the hidden textarea was never filled, and every save
        // posted an empty body that the form refused with a 400.
        this.editorContent = container.querySelector('.ui-content[contenteditable]');
        this.currentElement = null;
        this.currentBlock = null;
        this.selectedRange = null;
        
        this.init();
    }
    
    init() {
        this.initializeModals();
        this.initializeToolbarButtons();
        this.initializeEventListeners();
        this.setupFormSubmission();
    }
    
    // === Gestion de la sélection ===
    
    saveSelection() {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            this.selectedRange = selection.getRangeAt(0).cloneRange();
        }
    }
    
    restoreSelection() {
        if (this.selectedRange) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(this.selectedRange);
            this.selectedRange = null;
        }
    }
    
    // === Manipulation du contenu ===
    
    wrapSelection(element) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            element.appendChild(range.extractContents());
            range.insertNode(element);
            
            selection.removeAllRanges();
            const newRange = document.createRange();
            newRange.selectNodeContents(element);
            selection.addRange(newRange);
        }
    }
    
    unwrapSelection(tagName) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            let selectedElement = selection.getRangeAt(0).commonAncestorContainer;
            
            if (selectedElement.nodeType === Node.TEXT_NODE) {
                selectedElement = selectedElement.parentNode;
            }
            
            const elements = selectedElement.querySelectorAll(tagName);
            elements.forEach(el => {
                const textNode = document.createTextNode(el.textContent);
                el.replaceWith(textNode);
            });
            
            selection.removeAllRanges();
            const newRange = document.createRange();
            newRange.selectNodeContents(selectedElement);
            selection.addRange(newRange);
        }
    }
    
    insertBlock(element) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);

            if (range.commonAncestorContainer !== this.editorContent) {
                let e = range.commonAncestorContainer;
                while (e && e.parentNode !== this.editorContent) {
                    e = e.parentNode;
                }

                this.editorContent.insertBefore(element, e);
            } else {
                this.editorContent.appendChild(element);
            }
        } else {
            this.editorContent.appendChild(element);
        }

        selection.removeAllRanges();
        const newRange = document.createRange();
        const br = element.querySelector('br');
        if (br) {
            newRange.selectNode(br);
            newRange.collapse(false);
        } else {
            newRange.selectNodeContents(element);
            newRange.collapse(false);
        }
        selection.addRange(newRange);
    }
    
    replaceBlock(element) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            
            if (range.commonAncestorContainer !== this.editorContent) {
                let e = range.commonAncestorContainer;
                while (e && e.parentNode !== this.editorContent) {
                    e = e.parentNode;
                }
                
                element.innerHTML = e.innerHTML;
                e.replaceWith(element);
                
                selection.removeAllRanges();
                const newRange = document.createRange();
                newRange.selectNodeContents(element);
                newRange.collapse(false);
                selection.addRange(newRange);
            }
        }
    }
    
    // === Gestion des modales ===
    
    initializeModals() {
        this.initializeFigureModal();
        this.initializeAnchorModal();
        this.initializeEmbedModal();
    }
    
    initializeFigureModal() {
        const template = this.container.querySelector('template.insert-figure');
        if (template) {
            this.insertFigureModal = this.appendModal(template);
            
            this.insertFigureModal.addEventListener('submit', event => {
                event.preventDefault();
                const data = Object.fromEntries(new FormData(event.target));
                const selectedRadio = event.target.querySelector('input[name="src"]:checked');
                const alt = selectedRadio ? (selectedRadio.dataset.alt || '') : '';
                const html = `<figure><img src="${data.src}" alt="${alt}" /><figcaption><br /></figcaption></figure>`;
                const parser = new DOMParser();
                const document = parser.parseFromString(html, 'text/html');
                
                this.restoreSelection();
                this.insertBlock(document.body.firstChild);
                event.target.classList.remove('is-active');
            });
            
            this.insertFigureModal.addEventListener('reset', event => {
                event.target.classList.remove('is-active');
            });
        }
    }
    
    initializeAnchorModal() {
        const template = this.container.querySelector('template.wrap-anchor');
        if (template) {
            this.wrapAnchorModal = this.appendModal(template);
            
            this.wrapAnchorModal.addEventListener('submit', event => {
                event.preventDefault();
                const data = Object.fromEntries(new FormData(event.target));
                const html = `<a href="${data.href}" rel="${data.rel}" target="${data.target}"></a>`;
                const parser = new DOMParser();
                const document = parser.parseFromString(html, 'text/html');
                
                this.restoreSelection();
                this.wrapSelection(document.body.firstChild);
                event.target.classList.remove('is-active');
            });
            
            this.wrapAnchorModal.addEventListener('reset', event => {
                event.target.classList.remove('is-active');
            });
        }
    }
    
    initializeEmbedModal() {
        const template = this.container.querySelector('template.insert-embed');
        if (template) {
            this.insertEmbedModal = this.appendModal(template);
            
            this.insertEmbedModal.addEventListener('submit', event => {
                event.preventDefault();
                const data = Object.fromEntries(new FormData(event.target));
                const parser = new DOMParser();
                const document = parser.parseFromString(`<figure>${data.element}<figcaption><br /></figcaption></figure>`, 'text/html');
                
                this.restoreSelection();
                this.insertBlock(document.body.firstChild);
                event.target.classList.remove('is-active');
            });
            
            this.insertEmbedModal.addEventListener('reset', event => {
                event.target.classList.remove('is-active');
            });
        }
    }
    
    appendModal(template) {
        const wrapper = document.createElement('div');
        wrapper.appendChild(document.importNode(template.content, true));
        document.body.appendChild(wrapper);
        const modal = wrapper.firstElementChild;

        // Close on background click
        const bg = modal.querySelector('.ui-modal-background');
        if (bg) {
            bg.addEventListener('click', () => modal.classList.remove('is-active'));
        }

        return modal;
    }
    
    // === Gestion de la barre d'outils ===
    
    initializeToolbarButtons() {
        this.initializeBlockTypeSelector();
        this.initializeInlineFormatButtons();
        this.initializeBlockButtons();
        this.initializeLinkButtons();
    }
    
    initializeBlockTypeSelector() {
        const blockTypeSelect = this.container.querySelector('select.block-type');
        if (blockTypeSelect) {
            blockTypeSelect.addEventListener('change', event => {
                const element = document.createElement(event.target.value);
                this.replaceBlock(element);
            });
        }
    }
    
    initializeInlineFormatButtons() {
        const strongButton = this.container.querySelector('button.ui-button.strong');
        if (strongButton) {
            strongButton.addEventListener('click', () => {
                const element = document.createElement('strong');
                this.wrapSelection(element);
            });
        }
        
        const emButton = this.container.querySelector('button.ui-button.em');
        if (emButton) {
            emButton.addEventListener('click', () => {
                const element = document.createElement('em');
                this.wrapSelection(element);
            });
        }
        
        const removeStyleButton = this.container.querySelector('button.ui-button.remove-style');
        if (removeStyleButton) {
            removeStyleButton.addEventListener('click', () => {
                this.unwrapSelection('em');
                this.unwrapSelection('strong');
            });
        }
    }
    
    initializeBlockButtons() {
        const figureButton = this.container.querySelector('button.ui-button.figure');
        if (figureButton) {
            figureButton.addEventListener('click', () => {
                this.saveSelection();
                if (this.insertFigureModal) {
                    this.insertFigureModal.classList.add('is-active');
                }
            });
        }
        
        const embedButton = this.container.querySelector('button.ui-button.embed');
        if (embedButton) {
            embedButton.addEventListener('click', () => {
                this.saveSelection();
                if (this.insertEmbedModal) {
                    this.insertEmbedModal.classList.add('is-active');
                }
            });
        }
        
        const pButton = this.container.querySelector('button.ui-button.p');
        if (pButton) {
            pButton.addEventListener('click', () => {
                const element = document.createElement('p');
                element.appendChild(document.createElement('br'));
                this.insertBlock(element);
            });
        }
        
        const hrButton = this.container.querySelector('button.ui-button.hr');
        if (hrButton) {
            hrButton.addEventListener('click', () => {
                const element = document.createElement('hr');
                this.insertBlock(element);
            });
        }
    }
    
    initializeLinkButtons() {
        const linkButton = this.container.querySelector('button.ui-button.link');
        if (linkButton) {
            linkButton.addEventListener('click', () => {
                const selection = window.getSelection();
                if (!selection.isCollapsed) {
                    this.saveSelection();
                    if (this.wrapAnchorModal) {
                        this.wrapAnchorModal.classList.add('is-active');
                    }
                }
            });
        }
        
        const unlinkButton = this.container.querySelector('button.ui-button.unlink');
        if (unlinkButton) {
            unlinkButton.addEventListener('click', () => {
                this.unwrapSelection('a');
            });
        }
    }
    
    // === Gestion des événements ===
    
    initializeEventListeners() {
        this.container.addEventListener('currentElementChange', event => {
            const blockTypeSelect = this.container.querySelector('select.block-type');
            if (blockTypeSelect && event.detail.currentBlock) {
                blockTypeSelect.value = event.detail.currentBlock.tagName.toLowerCase();
            }
        });
        
        this.editorContent.addEventListener('click', () => this.updateBlockTypeSelect());
        this.editorContent.addEventListener('keyup', () => this.updateBlockTypeSelect());
        this.editorContent.addEventListener('keydown', () => this.updateBlockTypeSelect());
    }
    
    updateBlockTypeSelect() {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            
            if (range.commonAncestorContainer !== this.editorContent && 
                range.commonAncestorContainer !== this.currentElement) {
                
                this.currentElement = range.commonAncestorContainer;
                
                let e = this.currentElement;
                while (e && e.parentNode !== this.editorContent) {
                    e = e.parentNode;
                }
                
                this.currentBlock = e;
                
                this.container.dispatchEvent(new CustomEvent('currentElementChange', {
                    detail: {
                        currentElement: this.currentElement,
                        currentBlock: this.currentBlock
                    }
                }));
            }
        }
    }
    
    setupFormSubmission() {
        const form = this.container.closest('form');
        if (form) {
            form.addEventListener('submit', () => {
                const textarea = this.container.querySelector('textarea');
                if (textarea) {
                    textarea.value = this.editorContent.innerHTML;
                }
            });
        }
    }
}